import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { forgottenPasswordDialogAtom } from '../atoms';
import { useOnClickOutside } from '../hooks';
import { TOAST_DURATION } from '../lib/constants';
import { useForgotPasswordMutation } from '../lib/queries';
import { ForgotPasswordSchema } from '../lib/schemas';
import { Input } from './Input';
import { ForgotPassword } from '../types';

export function ForgotPasswordDialog() {
  const { t } = useTranslation();
  const [state, setState] = useAtom(forgottenPasswordDialogAtom);
  const dialog = useRef<HTMLDialogElement | null>(null);
  const { register, formState, getValues, handleSubmit } = useForm<ForgotPassword>({
    mode: 'all',
    resolver: zodResolver(ForgotPasswordSchema),
  });

  const forgotPasswordMutation = useForgotPasswordMutation();

  const handleClose = () => {
    setState({ isOpen: false, isSubmitted: false });
    dialog.current?.close();
  };

  const onSubmit = (data: ForgotPassword) => {
    forgotPasswordMutation.mutate(data, {
      onSuccess: () => {
        setState((prev) => ({ ...prev, isSubmitted: true }));
      },
      onError: (error: any) => {
        handleClose();
        toast.error(
          t('settings.passwordChangeError', { message: t(`apiError.${error.message}`) }),
          {
            duration: TOAST_DURATION,
          },
        );
      },
    });
  };

  useOnClickOutside(dialog.current, handleClose);

  useEffect(() => {
    if (!dialog.current?.open && state.isOpen) dialog.current?.showModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  return (
    <dialog ref={dialog}>
      <h1 className="text-title font-semibold">{t('forgotPassword.title')}</h1>
      {state.isSubmitted && (
        <>
          <span>
            <Trans
              i18nKey="forgotPassword.sentToEmail"
              values={{ email: getValues('email') }}
              components={{ b: <strong /> }}
            />
          </span>
          <button className="primary-fill" onClick={handleClose}>
            {t('common.fine')}
          </button>
        </>
      )}
      {!state.isSubmitted && (
        <>
          <span>{t('forgotPassword.emailDescription')}</span>
          <Input
            type="email"
            label="common.email"
            placeholder="email"
            {...register('email')}
            required
          />
          <button
            disabled={!formState.isValid}
            className="primary-fill"
            onClick={handleSubmit(onSubmit)}
          >
            {t('common.send')}
          </button>
        </>
      )}
    </dialog>
  );
}
